/**
 * Metin/HTML içindeki URL'leri yeni sekmede açılan tıklanabilir bağlantılara çevirir.
 *
 * İki kullanım vardır:
 *  - `linkifyText`  : düz metni React düğümlerine çevirir (URL'ler <a> olur).
 *  - `linkifyHtml`  : HTML string'i alır, yalnızca serbest metindeki URL'leri sarar
 *                     (zaten <a> içinde olanlara dokunmaz) ve HTML string döndürür.
 *
 * @module utils/linkify
 */

import React from "react";

// http/https ile başlayan veya www. ile başlayan URL'leri yakalar.
// Tek bir yakalama grubu içerir; bu sayede String.split ile bölündüğünde
// tek sayılı indeksler her zaman eşleşen URL'ler olur.
const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

// URL'in sonuna yapışmış noktalama işaretleri (cümle sonu vb.) bağlantıya dahil edilmez.
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

// www. ile başlayan adreslere protokol ekler; aksi halde tarayıcı relatif kabul eder.
const normalizeHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// Eşleşen URL'i, sonundaki noktalama işaretlerinden ayırır.
const splitTrailing = (match) => {
  const trailing = match.match(TRAILING_PUNCTUATION)?.[0] || "";
  const url = trailing ? match.slice(0, match.length - trailing.length) : match;
  return { url, trailing };
};

/**
 * Düz metindeki URL'leri tıklanabilir bağlantılara çevirir.
 * @param {string} text - İşlenecek düz metin
 * @returns {React.ReactNode} Metin ve <a> düğümlerinden oluşan dizi (veya orijinal değer)
 */
export const linkifyText = (text) => {
  if (!text || typeof text !== "string") {
    return text;
  }

  // Tek yakalama grubu sayesinde tek indeksler eşleşen URL'lerdir.
  return text.split(URL_REGEX).map((part, index) => {
    if (index % 2 === 0) {
      return part;
    }

    const { url, trailing } = splitTrailing(part);
    return (
      <React.Fragment key={index}>
        <a href={normalizeHref(url)} target="_blank" rel="noreferrer">
          {url}
        </a>
        {trailing}
      </React.Fragment>
    );
  });
};

/**
 * HTML string içindeki serbest metin URL'lerini tıklanabilir bağlantılara çevirir.
 * Zaten bir <a> etiketi içinde bulunan URL'lere dokunmaz.
 * @param {string} html - İşlenecek HTML string
 * @returns {string} URL'leri bağlantılanmış HTML string (veya orijinal değer)
 */
export const linkifyHtml = (html) => {
  if (!html || typeof html !== "string") {
    return html;
  }

  // DOMParser yalnızca tarayıcıda mevcut; erişilemezse metni olduğu gibi bırak.
  if (typeof window === "undefined" || !window.DOMParser) {
    return html;
  }

  const doc = new window.DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, window.NodeFilter.SHOW_TEXT);

  const textNodes = [];
  while (walker.nextNode()) {
    // Zaten bir bağlantının içindeki metinleri atla (çift sarma olmasın).
    if (!walker.currentNode.parentElement?.closest("a")) {
      textNodes.push(walker.currentNode);
    }
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue;
    URL_REGEX.lastIndex = 0;
    if (!URL_REGEX.test(text)) {
      return;
    }

    URL_REGEX.lastIndex = 0;
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match;

    while ((match = URL_REGEX.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(
          doc.createTextNode(text.slice(lastIndex, match.index))
        );
      }

      const { url, trailing } = splitTrailing(match[0]);
      const anchor = doc.createElement("a");
      anchor.href = normalizeHref(url);
      anchor.target = "_blank";
      anchor.rel = "noreferrer";
      anchor.textContent = url;
      fragment.appendChild(anchor);

      if (trailing) {
        fragment.appendChild(doc.createTextNode(trailing));
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode.replaceChild(fragment, node);
  });

  return doc.body.innerHTML;
};
